import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { uniqBy } from "lodash";
import { Row, Col, Steps } from "antd";
import { CloseOutlined, EditOutlined } from "@ant-design/icons";
import { BasicModal, Button } from "@life-line-lab/components-react";
import AssignDriverModal from "../parcel-tracking-modals/assign-driver-modal/assign-driver-modal";
import useAccessControlList from "Hooks/access-control.hook";
import { useSession } from "Hooks/index";
import DeliveryDateModal from "Components/order-management/shared/parcel-modals/delivery-date-modal";
import { TFunction } from "i18next";
import { Gutter } from "antd/lib/grid/row";

interface PlannedRoutesProps {
  current: number;
  t: TFunction;
  parcelDetails: any;
}

interface AssignButtonProps {
  showUpdateOption: boolean;
  handleOpenReassignDriver: () => void;
  isAssigned: boolean;
  t: TFunction;
}

interface FirstRowContentProps {
  parcelDetails: any;
  t: TFunction;
  rowGutter: [Gutter, Gutter];
}

interface SecondRowContentProps extends FirstRowContentProps {
  handleEditDeliveryDate: (id: string, value: string) => void;
}

interface ParcelTrackingPickupViewComponentProps {
  parcelDetails: any;
  vehicleOptions: any[];
  onAssignDriver: (data: any) => Promise<void>;
  onUpdateDeliveryDate: (data: any) => Promise<boolean>;
  isLoading: boolean;
}

const Header: React.FC<{ pickupCode: string }> = ({ pickupCode }) => {
  const history = useHistory();
  const onBack = () => {
    history.goBack();
  };

  return (
    <div className="dm-put-header">
      <div className="put-title">{pickupCode}</div>
      <div className="put-close">
        <CloseOutlined
          style={{ fontSize: "20px" }}
          onClick={() => {
            onBack();
          }}
          className="cursor-pointer back-action right"
        />
      </div>
    </div>
  );
};

const AssignButton: React.FC<AssignButtonProps> = ({
  showUpdateOption,
  handleOpenReassignDriver,
  isAssigned,
  t,
}) => {
  return (
    <div className="dm-put-btn-container">
      {showUpdateOption && (
        <Button
          className="mr-5"
          type="default"
          label={
            isAssigned ? t("action.reassignDriver") : t("action.assignDriver")
          }
          size="large"
          onClick={handleOpenReassignDriver}
        />
      )}
    </div>
  );
};

const PlannedRoutes: React.FC<PlannedRoutesProps> = ({
  current,
  t,
  parcelDetails,
}) => {
  const { Step } = Steps;
  const uniqueDropOffData: any = uniqBy(
    parcelDetails?.dropOffLocationData,
    "_id",
  );
  return (
    <div className="dm-put-planned-route">
      <div>
        <Row gutter={[0, 16]}>
          <Col span={4}>
            <div className="put-title">{t("label.plannedRoute")}</div>
          </Col>
          <Col span={20}>
            <Steps current={current}>
              <Step
                className="put-step-item put-step-customer"
                disabled={true}
                title="Customer"
                description={new Date(
                  parcelDetails?.pickupTimeFrom,
                ).toLocaleTimeString()}
              />
              {uniqueDropOffData?.map((location, index) => {
                return (
                  <Step
                    className="put-step-item"
                    disabled={true}
                    title={location?.locationName}
                    description={`Dropoff location ${index + 1}`}
                    key={index}
                  />
                );
              })}
            </Steps>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const FirstRowContent: React.FC<FirstRowContentProps> = ({
  parcelDetails,
  t,
  rowGutter,
}) => {
  const locationAndTrackingData = parcelDetails?.pickupTasks?.map(
    (task, index) => ({
      ...task,
      ...parcelDetails?.locationData[index],
    }),
  );

  return (
    <Row gutter={rowGutter}>
      <Col span={6}>
        <label className="title"> {t("label.customerAndLocation")} </label>
      </Col>
      <Col span={5}>
        <label className="title"> {t("label.operatingHours")} </label>
      </Col>
      <Col span={5}>
        <label className="title"> {t("label.estPickupTime")} </label>
      </Col>
      <Col span={4}>
        <label className="title"> {t("label.orderNo")} </label>
      </Col>
      <Col span={4}>
        <label className="title"> {t("label.trackingNo")} </label>
      </Col>
      {locationAndTrackingData?.map((customer, index) => {
        return (
          <>
            <Col span={6}>
              <div className="put-name">{customer?.contactPerson}</div>
              <div className="put-address">
                <div>
                  {customer?.address?.street1 + ", "}
                  {customer?.address?.street2
                    ? customer?.address?.street2 + ", "
                    : ""}
                </div>
                <div>
                  {customer?.address?.postcode + ", "}
                  {customer?.address?.city + ", "}
                </div>
                <div>
                  {customer?.address?.state + ", "}
                  {customer?.address?.country}
                </div>
              </div>
            </Col>
            <Col span={5}>
              {customer?.workingHours?.map((hour, index) => {
                return (
                  <Row key={index}>
                    <Col span={24} xxl={8}>
                      <div className="label">
                        {hour?.day?.charAt(0) +
                          hour?.day?.substring(1).toLowerCase()}
                        :
                      </div>
                    </Col>
                    <Col span={24} xxl={16}>
                      {moment(hour?.openingTime).format("hh:mm a")} -{" "}
                      {moment(hour?.closingTime).format("hh:mm a")}
                    </Col>
                  </Row>
                );
              })}
            </Col>
            <Col span={5}>
              <div>
                {moment(parcelDetails?.pickupDate).format("YYYY-MM-DD")} |{" "}
                {moment(parcelDetails?.pickupTimeFrom).format("hh:mm a")}
              </div>
              <div>{customer?.pickupInstructions || <span>&#11835;</span>}</div>
            </Col>
            <Col span={4}>
              <div>{customer?.taskCode}</div>
            </Col>
            <Col span={4}>
              {customer?.parcelList?.map((parcel, index) => (
                <div key={index}>{parcel?.trackingNumber}</div>
              ))}
            </Col>
          </>
        );
      })}
      <Col span={24}></Col>
    </Row>
  );
};

const SecondRowContent: React.FC<SecondRowContentProps> = ({
  handleEditDeliveryDate,
  parcelDetails,
  t,
  rowGutter,
}) => {
  return (
    <Row gutter={rowGutter}>
      <Col span={6}>
        <label className="title"> {t("label.noOfParcels")} </label>
      </Col>
      <Col span={5}>
        <label className="title">{t("label.vehicleType")}</label>
      </Col>
      <Col span={5}>
        <label className="title"> {t("label.driverAssigned")} </label>
      </Col>
      <Col span={4}>
        <label className="title"> {t("label.deliveryDate")} </label>
      </Col>

      <Col span={6}>
        <div>{parcelDetails?.noOfParcels ?? t("txt.unavailable")}</div>
      </Col>
      <Col span={5}>
        <div>
          {parcelDetails?.vehicleData?.name +
            " (" +
            parcelDetails?.vehicleData?.registrationNo +
            ")"}
        </div>
      </Col>
      <Col span={5}>
        <div>
          {parcelDetails?.assignedDriverData?.fullName ??
            t("label.unavailable")}
        </div>
      </Col>
      <Col span={4}>
        {parcelDetails?.deliveryDate && (
          <div className="pov-edit-container">
            <div>
              {moment(parcelDetails?.deliveryDate).format("DD-MM-YYYY") || "-"}
            </div>
            <span
              className="pov-edit"
              onClick={() =>
                handleEditDeliveryDate(
                  parcelDetails?._id,
                  parcelDetails?.deliveryDate,
                )
              }
            >
              <EditOutlined />
            </span>
          </div>
        )}
      </Col>
    </Row>
  );
};

const ParcelTrackingPickupViewComponent = ({
  parcelDetails,
  vehicleOptions,
  onAssignDriver,
  onUpdateDeliveryDate,
  isLoading,
}: ParcelTrackingPickupViewComponentProps) => {
  const { t } = useTranslation();
  const { roleTypes } = useSession();
  const [callModal, setCallModal] = useState<any>(<></>);
  const initialModal: any = {
    open: false,
  };
  const [modal, setModal] = useState<any>(initialModal);
  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Delivery_Management",
  });
  const showUpdateOption = permission.rights.includes("UPDATE");
  const [error, setError] = useState<any>({});
  const rowGutter: [Gutter, Gutter] = [16, 16];
  const [current, setCurrent] = useState<number>(0);

  useEffect(() => {
    manualCheckingOfLocationStatus();
  }, [parcelDetails]);

  // track location in which the parcel has been picked up, as a stepper
  const manualCheckingOfLocationStatus = () => {
    const tasks = parcelDetails?.pickUpTasks;
    const length = tasks?.length ?? 0;

    // if there are no tasks, dont bother checking
    if (!tasks) return;

    // if there is no status, dont bother checking
    if (!parcelDetails?.status) return;

    switch (parcelDetails.status) {
      // if the parcel is still OPEN, the pick up has not started
      case "OPEN":
        setCurrent(0);
        break;
      // if the parcel is in PICKED_UP or CLOSED, the pick up has been completed
      case "PICKED_UP":
      case "CLOSED":
        setCurrent(length);
        break;
      // otherwise, the parcel is in progress (PICKUP_IN_PROGRESS)
      default:
        // go through every parcel, and stop when the first parcel without open is seen
        for (let x = 0; x < length; x++) {
          if (tasks[x]?.status === "OPEN") {
            setCurrent(x);
          }
        }
    }
  };

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleAssignDriver = (inputValue) => {
    onAssignDriver(inputValue);
    handleResetModalState();
  };

  const handleOpenReassignDriver = () => {
    setCallModal(
      <AssignDriverModal
        selectedRows={[parcelDetails._id]}
        vehicleOptions={vehicleOptions}
        onSubmit={handleAssignDriver}
        info={[parcelDetails]}
        type="REASSIGN"
        handleCancel={handleResetModalState}
      />,
    );
    setModal({ open: true, type: "REASSIGN" });
  };

  const handleOnUpdateDeliveryDate = async (data) => {
    const isSuccess = await onUpdateDeliveryDate(data);

    if (isSuccess) {
      handleResetModalState();
    } else {
      setError((prev) => ({
        ...prev,
        general: "Failed to update delivery date",
      }));
    }
  };

  const modalProps = {
    onSubmit: handleOnUpdateDeliveryDate,
    onClose: handleResetModalState,
    error,
    setError,
    isLoading,
  };

  const handleEditDeliveryDate = (id: string, value: string) => {
    if (!value) return;
    if (!id) return;

    const date = moment(value);

    setCallModal(
      <DeliveryDateModal
        {...modalProps}
        initialDeliveryDate={date}
        orderID={id}
      />,
    );
    setModal((prev) => ({ ...prev, open: true, orderID: id }));
  };

  return (
    <div className="dm-put-container pick-up">
      <Header pickupCode={parcelDetails.pickupCode} />
      <div className="dm-divider"></div>
      <PlannedRoutes current={current} t={t} parcelDetails={parcelDetails} />
      <AssignButton
        showUpdateOption={showUpdateOption}
        handleOpenReassignDriver={handleOpenReassignDriver}
        isAssigned={parcelDetails?.assignedDriver ? true : false}
        t={t}
      />
      <div className="dm-put-content">
        <FirstRowContent
          parcelDetails={parcelDetails}
          t={t}
          rowGutter={rowGutter}
        />
        <SecondRowContent
          parcelDetails={parcelDetails}
          handleEditDeliveryDate={handleEditDeliveryDate}
          t={t}
          rowGutter={rowGutter}
        />
      </div>
      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
        width={800}
      >
        {callModal}
      </BasicModal>
    </div>
  );
};

export default ParcelTrackingPickupViewComponent;
